'use client';
import SolarCheckCircleBold from '@/components-v2/icons/SolarCheckCircleBold';
import SolarCloseCircleOutline from '@/components-v2/icons/SolarCloseCircleOutline';
import SolarShareBold from '@/components-v2/icons/SolarShareBold';
import { useToast } from '@/components/toast';
import { APPBAR_HEIGHT } from '@/layout/base-layout/constants';
import transitions from '@/theme/core/transitions';
import { Global } from '@emotion/react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { grey } from '@mui/material/colors';
import { styled } from '@mui/material/styles';
import { useTranslate } from '@tolgee/react';
import { PropsWithChildren, useState } from 'react';

const drawerBleeding = 24;

const StyledBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
}));

const OuterContainer = styled(Box)({
  height: '100%',
  paddingLeft: 16,
  paddingRight: 16,
});

const InnerContainer = styled(Box)(({ theme }) => ({
  height: '100%',
  // borderTopLeftRadius: 24,
  // borderTopRightRadius: 24,
  backgroundColor: theme.palette.background.paper,
  overflow: 'auto',
  position: 'relative',
}));

const Puller = styled(Box)(({ theme }) => ({
  height: 6,
  backgroundColor: grey[300],
  borderRadius: 3,
  position: 'absolute',
  top: 9,
  left: '50%',
  transform: 'translateX(-50%)',
  width: 48,
  display: 'block',
  [theme.breakpoints.up('sm')]: {
    width: 120,
    display: 'none',
  },
  ...theme.applyStyles('dark', {
    backgroundColor: grey[900],
  }),
}));

// 共享的按钮样式配置
const getDrawerButtonStyles = (position: 'left' | 'right') => (theme: any) => ({
  position: 'absolute',
  top: -drawerBleeding,
  [position]: { xs: 36, sm: 72 },
  transform: 'translateY(-50%)',
  zIndex: 10,
  minWidth: 'auto',
  height: { xs: 32, sm: 28 },
  borderRadius: '16px',
  border: `2px solid ${theme.palette.background.paper}`,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  fontSize: '13px',
  fontWeight: 500,
  pl: { xs: 0.5, sm: 0.25 },
  pr: { xs: 1, sm: 0.75 },
  transition: `all ${transitions.durations.short}ms ${transitions.easings.emphasized}`,
  '& .MuiButton-startIcon': {
    marginRight: { xs: 0.5, sm: 0.25 },
    marginLeft: 0,
  },
  '&:active': {
    transform: 'translateY(-50%) scale(0.98)',
  },
});

const CloseButton = ({ onClose, hide }: { onClose?: () => void; hide?: boolean }) => {
  const { t } = useTranslate();

  if (hide) return null;

  return (
    <Button
      onClick={onClose}
      startIcon={
        <Box component={SolarCloseCircleOutline} width={16} height={16} sx={{ fontSize: { xs: 16, sm: 14 } }} />
      }
      sx={(theme) => ({
        ...getDrawerButtonStyles('right')(theme),
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '@media (hover: hover)': {
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            transform: 'translateY(-50%) scale(1.05)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
          },
        },
      })}
    >
      {t('common.close')}
    </Button>
  );
};

const ShareButton = ({ hide }: { hide?: boolean }) => {
  const { t } = useTranslate();
  const { showToast } = useToast();
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    try {
      const currentUrl = window.location.href;
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);

      showToast('common.share.copied', 'success');

      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy URL:', error);
    }
  };

  if (hide) return null;

  return (
    <Button
      onClick={handleShare}
      startIcon={
        <Box
          component={copied ? SolarCheckCircleBold : SolarShareBold}
          width={16}
          height={16}
          sx={{ fontSize: { xs: 16, sm: 14 } }}
        />
      }
      sx={(theme) => ({
        ...getDrawerButtonStyles('left')(theme),
        backgroundColor: copied ? theme.palette.success.main : theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
        '@media (hover: hover)': {
          '&:hover': {
            backgroundColor: copied ? theme.palette.success.dark : theme.palette.secondary.dark,
            transform: 'translateY(-50%) scale(1.05)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
          },
        },
      })}
    >
      {t('common.share')}
    </Button>
  );
};

interface BottomModalDrawerProps extends PropsWithChildren {
  open: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  swipeAreaWidth?: number;
  disableSwipeToOpen?: boolean;
  hideCloseButton?: boolean;
  hideShareButton?: boolean;
}

export function BottomModalDrawer({
  children,
  open,
  onClose = () => {
    history.back();
  },
  onOpen = () => {},
  swipeAreaWidth = drawerBleeding,
  disableSwipeToOpen = false,
  hideCloseButton = false,
  hideShareButton = true, // 默认隐藏分享按钮
}: BottomModalDrawerProps) {
  // 彻底清理：完全不渲染 drawer 当关闭时
  if (!open) return null;

  return (
    <>
      <Global
        styles={{
          '.MuiDrawer-root > .MuiPaper-root': {
            height: `calc(100% - ${APPBAR_HEIGHT}px - ${drawerBleeding}px)`,
            overflow: 'visible',
          },
        }}
      />
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        onOpen={onOpen}
        swipeAreaWidth={swipeAreaWidth}
        disableSwipeToOpen={disableSwipeToOpen}
        keepMounted={false}
        PaperProps={{
          style: {
            top: `${APPBAR_HEIGHT + drawerBleeding}px`,
          },
        }}
      >
        <CloseButton onClose={onClose} hide={hideCloseButton} />
        <ShareButton hide={hideShareButton} />
        <StyledBox
          sx={{
            position: 'absolute',
            top: -drawerBleeding,
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            visibility: 'visible',
            right: 0,
            left: 0,
            height: drawerBleeding,
          }}
        >
          <Puller />
        </StyledBox>
        <OuterContainer>
          <InnerContainer>{children}</InnerContainer>
        </OuterContainer>
      </SwipeableDrawer>
    </>
  );
}
